// eslint-disable-next-line jira/filename-case
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useCurrentProduct } from '@jira/growth__invite-prompt-on-link-insert/src/utils/index.tsx';
import { useCallback } from 'react';
import type { AccessRequestType } from '../../common/types.tsx';
import { requestAccess, useRequestAccessService } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import {
	cjnEligibilityCacheHandler,
	setHasSeenNudgeInLast7Days,
	useSnooze,
} from '../../services/caching/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useNudgeStateJiraToJpd } from '../../services/nudge-state/index.tsx';
import { useRequestAccessErrorFlag } from '../request-access-error-flag/index.tsx';
import {
	getAnalyticsAttributes,
	getJPDCJNMessageStrings,
	useRequestAccessPromptFlag,
} from '../request-access-prompt-flag/index.tsx';
import { useRequestAccessSuccessFlag } from '../request-access-success-flag/index.tsx';

const MAX_NUMBER_OF_JPD_COLLABORATORS = 10;

type UseJpdCrossJoinNudgeArguments = {
	featureVisitedCount: number;
};

export const useJpdCrossJoinNudge = (): (({
	featureVisitedCount,
}: UseJpdCrossJoinNudgeArguments) => Promise<void>) => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const currentProduct = useCurrentProduct();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'jira-product-discovery',
		MAX_NUMBER_OF_JPD_COLLABORATORS,
	);
	const showRequestAccessPromptFlag = useRequestAccessPromptFlag();
	const showSuccessFlag = useRequestAccessSuccessFlag();
	const showErrorFlag = useRequestAccessErrorFlag();
	const { push } = useRouterActions();
	const { snoozeFor30Days, increaseVisitedCount } = useSnooze('crossJoinNudgesJiraToJpd');
	const { setNudgeDismissed } = useNudgeStateJiraToJpd();
	const requestAccessService = useRequestAccessService();

	return useCallback(
		async ({ featureVisitedCount }: UseJpdCrossJoinNudgeArguments) => {
			try {
				const startTimestamp = Date.now();
				const collaboratorsOnJpd = await getFilteredCollaborators();
				const onSubmit = async () => {
					try {
						let accessStatus: AccessRequestType;

						if (requestAccessService) {
							const serviceResult = await requestAccessService({
								product: 'jira-product-discovery',
								integration: 'crossJoinNudgesJiraToJpd',
								touchpoint: 'Crossjoin Nudge',
							});

							if (serviceResult.error) {
								fireErrorAnalytics('crossJoinNudgesJiraToJpd', serviceResult.error, { cloudId });
								throw serviceResult.error;
							}

							accessStatus = serviceResult.result;
						} else {
							accessStatus = await requestAccess(
								'jira-product-discovery',
								currentProduct,
								cloudId,
								userId,
								'crossJoinNudgesJiraToJpd',
								createAnalyticsEvent,
							);
						}

						if (
							accessStatus === 'ACCESS_GRANTED' ||
							accessStatus === 'ACCESS_EXISTS' ||
							accessStatus === 'APPROVED_REQUEST_EXISTS'
						) {
							push('/jira/projects');
						} else if (
							accessStatus === 'PENDING_REQUEST_CREATED' ||
							accessStatus === 'PENDING_REQUEST_EXISTS'
						) {
							showSuccessFlag();
						} else {
							showErrorFlag('jira-product-discovery');
						}
					} catch {
						showErrorFlag('jira-product-discovery');
					}
				};

				const runPostDisplaySideEffects = () =>
					Promise.all([
						increaseVisitedCount(featureVisitedCount + 1),
						cjnEligibilityCacheHandler.set('has_seen_jpd', { value: true }),
						setHasSeenNudgeInLast7Days(),
					]);

				const promptFlagArgs = {
					users: collaboratorsOnJpd,
					onSubmit,
					onOptout: setNudgeDismissed,
					snoozeFor30Days,
					integration: 'crossJoinNudgesJiraToJpd' as const,
					featureVisitedCount,
					messageParser: getJPDCJNMessageStrings,
					eventAttributes: getAnalyticsAttributes({
						userId,
						users: collaboratorsOnJpd,
						featureVisitedCount,
						startTimestamp,
					}),
				};

				const onDisplayed = () => {
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					runPostDisplaySideEffects().catch((error: any) => {
						fireErrorAnalytics('crossJoinNudgesJiraToJpd', error, { cloudId });
					});
				};
				showRequestAccessPromptFlag({ ...promptFlagArgs, onDisplayed });
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics('crossJoinNudgesJiraToJpd', error, { cloudId });
			}
		},
		[
			cloudId,
			currentProduct,
			createAnalyticsEvent,
			getFilteredCollaborators,
			increaseVisitedCount,
			push,
			requestAccessService,
			setNudgeDismissed,
			showErrorFlag,
			showRequestAccessPromptFlag,
			showSuccessFlag,
			snoozeFor30Days,
			userId,
		],
	);
};
